// Library Methods
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useCallback, useContext, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AddCommentModal from "./AddCommentModal/AddCommentModal";
import { useTheme } from "@mui/material/styles";
import { SpeechBubbleIcon } from "src/assets/icons";

// Utils
import { getDecimalNum } from "src/utils/number/numberHelper";
import { calculateYearDifference } from "src/utils/date/dateHelper";

// Constants
import { examCardEnums, eyeTypeEnums } from "src/constants/enums";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { ReportContext } from "src/contexts/reports/ReportContext";

// Styles
import {
  ModalCustomConfirmBlack,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { useAddExamReportComment } from "src/services/exams/hooks/exam-reports/useAddExamReportComment";
import { useAddProgressionAnalysisComment } from "src/services/exams/hooks/progression-reports/useAddProgressionAnalysisComment";

const ReportPatientHeader = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { outline } = getSurfaceTokens(theme);

  // Same reasoning as ModalCustomConfirmBlack -- a solid black badge
  // disappears against a dark page, so flip to a light surface in dark mode.
  const eyeBadgeBackground = isDark ? "#F5F6F8" : "#000";
  const eyeBadgeColor = isDark ? "#12141C" : "#FFF";

  const { clinicInfo } = useClinicContext();
  const { examReport, eyeType, sectionId } = useContext(ReportSectionContext);
  const { patient, exam, externalExam, progressionAnalysis } =
    useContext(ReportContext);

  const [openCommentModal, setOpenCommentModal] = useState(false);

  const isProgressionAnalysis = useMemo(
    () =>
      location.pathname.includes("progression-analysis") ||
      location.search.includes("progression_analysis_id"),
    [location.pathname, location.search]
  );

  const { mutateAsync: addExamReportComment } = useAddExamReportComment(
    clinicInfo?.id
  );
  const { mutateAsync: addProgressionReportComment } =
    useAddProgressionAnalysisComment(clinicInfo?.id);

  const eyeAbbreviation = useMemo(() => {
    const { RIGHT, BOTH } = eyeTypeEnums;

    switch (eyeType) {
      case BOTH:
        return "OU";
      case RIGHT:
        return "OD";
      default:
        return "OS";
    }
  }, [eyeType]);

  const examType = useMemo(() => {
    const enumValues = Object.values(examCardEnums);
    const examLabel = enumValues.find((val) =>
      val.type.includes(examReport?.exam?.type)
    )?.translatedType;
    return t(examLabel ?? "");
  }, [examReport?.exam?.type, t]);

  const examAlgorithm = useMemo(
    () => t(examReport?.exam?.algorithm),
    [examReport?.exam?.algorithm, t]
  );

  const examTypeText = useMemo(() => {
    if (examType && examAlgorithm) {
      return examType === examAlgorithm
        ? examType
        : `${examType} - ${examAlgorithm}`;
    } else if (isProgressionAnalysis) {
      return t("Progression Analysis");
    } else if (externalExam) {
      return externalExam?.examType
        ? `${t("External Exam")} - ${t(externalExam?.examType)}`
        : t("External Exam");
    }

    return "";
  }, [examType, examAlgorithm, isProgressionAnalysis, externalExam, t]);

  const patientBirthdate = useMemo(
    () => patient?.dateOfBirth?.slice(0, 10) ?? "",
    [patient?.dateOfBirth]
  );

  const patientId = useMemo(
    () =>
      typeof patient?.healthCareId === "string" &&
      patient?.healthCareId?.length > 0
        ? patient?.healthCareId
        : t("Blank"),
    [patient?.healthCareId, t]
  );

  const patientAge = useMemo(
    () => calculateYearDifference(patient?.dateOfBirth, clinicInfo?.timezone),
    [patient?.dateOfBirth, clinicInfo?.timezone]
  );

  const emptyLeftPrescription = useMemo(
    () =>
      patient?.leftSphere === null &&
      patient?.leftCylinder === null &&
      patient?.leftAxis === null,
    [patient?.leftSphere, patient?.leftCylinder, patient?.leftAxis]
  );

  const emptyRightPrescription = useMemo(
    () =>
      patient?.rightSphere === null &&
      patient?.rightCylinder === null &&
      patient?.rightAxis === null,
    [patient?.rightSphere, patient?.rightCylinder, patient?.rightAxis]
  );

  const addCommentWrapper = useCallback(
    async (commentData: {
      content: string;
      conditions?: string | undefined;
    }) => {
      if (isProgressionAnalysis && patient?.id && progressionAnalysis?.id) {
        return await addProgressionReportComment({
          patientId: patient?.id,
          progressionAnalysisId: progressionAnalysis?.id,
          commentData,
        });
      } else if (!isProgressionAnalysis && patient?.id && exam?.id) {
        return await addExamReportComment({
          patientId: patient?.id,
          examId: exam?.id,
          sectionId,
          commentData,
        });
      }
    },
    [
      addExamReportComment,
      addProgressionReportComment,
      isProgressionAnalysis,
      progressionAnalysis?.id,
      exam?.id,
      patient?.id,
      sectionId,
    ]
  );

  return (
    <Grid container sx={{ display: "flex", alignItems: "start" }}>
      <Grid container item xs={12}>
        <Grid container item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: `1px solid ${outline}`,
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "60px",
                height: "100%",
                color: eyeBadgeColor,
                background: eyeBadgeBackground,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {eyeAbbreviation}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "8px",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: { xs: "0px", sm: "8px" },
                }}
              >
                {/* Patient information */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: { xs: "100%", sm: "60%" },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      maxWidth: "100%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t("Name")}: {patient?.firstName} {patient?.lastName}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      columnGap: "8px",
                    }}
                  >
                    <Typography fontSize="13px">
                      <span style={{ fontWeight: 600 }}>{t("Sex")}:</span>{" "}
                      {t(patient?.sex ?? "")}
                    </Typography>
                    <Typography fontSize="13px">
                      <span style={{ fontWeight: 600 }}>{t("Age")}:</span>{" "}
                      {patientAge}
                    </Typography>
                    <Typography fontSize="13px">
                      <span style={{ fontWeight: 600 }}>{t("Birthdate")}:</span>{" "}
                      {patientBirthdate}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      columnGap: "8px",
                    }}
                  >
                    <Typography fontSize="13px">
                      <span style={{ fontWeight: 600 }}>
                        {t("Healthcare Id")}:
                      </span>{" "}
                      {patientId}
                    </Typography>
                  </Box>

                  <Box display="flex" flexDirection="column" width="100%">
                    <Typography fontSize="13px">
                      <span
                        style={{ fontWeight: "bold" }}
                      >{`${t("OD Rx")}: `}</span>
                      {emptyRightPrescription ? (
                        <span>{t("No Prescription Entered")}</span>
                      ) : (
                        <>
                          <span>
                            {patient && patient?.rightSphere !== null
                              ? getDecimalNum(patient?.rightSphere, 2)
                              : "N/A"}
                            {" sph | "}
                            {patient && patient?.rightCylinder !== null
                              ? getDecimalNum(patient?.rightCylinder, 2)
                              : "N/A"}
                            {" cyl | "}
                            {patient && patient?.rightAxis !== null
                              ? `${getDecimalNum(patient?.rightAxis, 0)}°`
                              : "N/A"}
                            {` ${t("axis")}`}
                          </span>
                        </>
                      )}
                    </Typography>

                    <Typography fontSize="13px">
                      <span
                        style={{ fontWeight: "bold" }}
                      >{`${t("OS Rx")}: `}</span>
                      {emptyLeftPrescription ? (
                        <span>{t("No Prescription Entered")}</span>
                      ) : (
                        <>
                          <span>
                            {patient && patient?.leftSphere !== null
                              ? getDecimalNum(patient?.leftSphere, 2)
                              : "N/A"}
                            {" sph | "}
                            {patient && patient?.leftCylinder !== null
                              ? getDecimalNum(patient?.leftCylinder, 2)
                              : "N/A"}
                            {" cyl | "}
                            {patient && patient?.leftAxis !== null
                              ? `${getDecimalNum(patient?.leftAxis, 0)}°`
                              : "N/A"}
                            {` ${t("axis")}`}
                          </span>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Clinic information */}
                <Box
                  sx={{
                    display: "flex",
                    width: { xs: "100%", sm: "40%" },
                    flexDirection: "column",
                    alignItems: { xs: "flex-start", sm: "flex-end" },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      maxWidth: "100%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {clinicInfo?.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      maxWidth: "100%",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {examTypeText}
                  </Typography>
                </Box>
              </Box>

              {/* Add comment button */}
              {!externalExam && (
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    gap: "8px",
                    justifyContent: "flex-end",
                  }}
                >
                  {!externalExam && (
                    <Button
                      startIcon={<SpeechBubbleIcon />}
                      sx={{
                        ...ModalCustomConfirmBlack(theme),
                        width: { xs: "100%", sm: "fit-content" },
                      }}
                      onClick={() => setOpenCommentModal(true)}
                    >
                      <Typography
                        sx={{ fontSize: "12px", textTransform: "none" }}
                      >
                        {t("Add Comment")}
                      </Typography>
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <AddCommentModal
        open={openCommentModal}
        onClose={() => setOpenCommentModal(false)}
        submitComment={addCommentWrapper}
      />
    </Grid>
  );
};

export default ReportPatientHeader;
